import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Checkbox, Col, DatePicker, Input, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import { useCrmDemos } from '@/hooks/useCrmDemos';
import { CRM_DEMO_TYPES, type CrmDemo, type CrmDemoListFilters, type CrmDemoStatus } from '@/services/crmDemo.service';
import { crmPersonName } from '@/components/crm/activities/CrmActivityTimeline';
import { formatDate } from '@/utils/format/date';

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

export const DEMO_STATUS_COLOR: Record<CrmDemoStatus, string> = {
  Planned: 'blue',
  Done: 'green',
  Cancelled: 'default',
};

const DemoRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const canCreate = Boolean(user?.is_super_admin || user?.permissions?.includes('crm.demo.create'));

  const [status, setStatus] = useState<string | undefined>();
  const [demoType, setDemoType] = useState<string | undefined>();
  const [presenter, setPresenter] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [hasGaps, setHasGaps] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const { data: users = [] } = useUsers({ is_active: true });

  const filters: CrmDemoListFilters = useMemo(
    () => ({
      status,
      demo_type: demoType,
      presenter_user_id: presenter,
      date_from: range?.[0]?.format('YYYY-MM-DD'),
      date_to: range?.[1]?.format('YYYY-MM-DD'),
      has_gaps: hasGaps || undefined,
      search: search.trim() || undefined,
      page,
      limit: pageSize,
    }),
    [status, demoType, presenter, range, hasGaps, search, page, pageSize]
  );
  const { data, isLoading } = useCrmDemos(filters);
  const totals = data?.totals;

  const columns: ColumnsType<CrmDemo> = [
    {
      title: 'Demo',
      dataIndex: 'demo_number',
      width: 140,
      render: (v: string, r) => <Link to={`/growth/demos/${r.id}`}>{v}</Link>,
    },
    { title: 'Date', dataIndex: 'demo_date', width: 110, render: (d: string) => formatDate(d) },
    {
      title: 'Customer / opportunity',
      key: 'record',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text>{r.customer_name || '-'}</Text>
          {r.opportunity && (
            <Link to={`/growth/opportunities/${r.opportunity.id}`}>
              {r.opportunity.opportunity_number} — {r.opportunity.opportunity_name}
            </Link>
          )}
          {!r.opportunity && r.lead && <Link to={`/growth/leads/${r.lead.id}`}>{r.lead.lead_number}</Link>}
        </Space>
      ),
    },
    { title: 'Title', dataIndex: 'title', ellipsis: true },
    { title: 'Type', dataIndex: 'demo_type', width: 90 },
    { title: 'Presenter', dataIndex: 'presenter_name', width: 150 },
    {
      title: 'Fit / Partial / Gap',
      key: 'fit',
      width: 160,
      render: (_, r) =>
        r.requirement_count ? (
          <Space size={2}>
            <Tag color="green">{r.fit}</Tag>
            <Tag color="gold">{r.partial}</Tag>
            <Tag color={r.gap ? 'red' : 'default'}>{r.gap}</Tag>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Open commitments',
      dataIndex: 'open_commitments',
      width: 130,
      align: 'center',
      render: (v: number) => (v ? <Tag color="purple">{v}</Tag> : <Text type="secondary">0</Text>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: CrmDemoStatus) => <Tag color={DEMO_STATUS_COLOR[s]}>{s}</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Demos &amp; requirements
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Every product demo, the requirements the customer raised, how well we fit them and what we committed to.
          </Paragraph>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/growth/demos/new')}>
            New demo
          </Button>
        )}
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Planned" value={totals?.planned ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Done" value={totals?.done ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Requirements" value={totals?.requirement_count ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Fit" value={totals?.fit ?? 0} valueStyle={{ color: '#389e0d' }} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Partial / Gap" value={`${totals?.partial ?? 0} / ${totals?.gap ?? 0}`} />
          </Card>
        </Col>
        <Col xs={12} md={4}>
          <Card size="small">
            <Statistic title="Open commitments" value={totals?.open_commitments ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker format="DD-MM-YYYY" value={range} onChange={(v) => { setRange(v); setPage(1); }} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={['Planned', 'Done', 'Cancelled'].map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            placeholder="Type"
            style={{ width: 130 }}
            value={demoType}
            onChange={(v) => { setDemoType(v); setPage(1); }}
            options={CRM_DEMO_TYPES.map((s) => ({ value: s, label: s }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Presenter"
            style={{ width: 200 }}
            value={presenter}
            onChange={(v) => { setPresenter(v); setPage(1); }}
            options={users.map((u) => ({ value: u.id, label: crmPersonName(u) }))}
          />
          <Input.Search
            allowClear
            placeholder="Search number, title, customer, modules"
            style={{ width: 280 }}
            onSearch={(v) => { setSearch(v); setPage(1); }}
          />
          <Checkbox checked={hasGaps} onChange={(e) => { setHasGaps(e.target.checked); setPage(1); }}>
            With gaps only
          </Checkbox>
        </Space>
      </Card>

      <Card size="small">
        <Table<CrmDemo>
          rowKey="id"
          size="small"
          loading={isLoading}
          columns={columns}
          dataSource={data?.items ?? []}
          onRow={(r) => ({ onDoubleClick: () => navigate(`/growth/demos/${r.id}`) })}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      </Card>
    </div>
  );
};

export default DemoRegisterPage;
